import { useState, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Building2, 
  Users, 
  Package, 
  Activity,
  Crown,
  AlertTriangle,
  Lock,
  Unlock,
  CheckCircle,
  Star,
  Download
} from 'lucide-react';

export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [visitorStats, setVisitorStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'super_admin') {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [statsRes, orgsRes, visitorsRes] = await Promise.all([
        api.get('/admin/system-stats'),
        api.get('/admin/organizations'),
        api.get('/admin/visitor-stats?days=7')
      ]);
      setStats(statsRes.data);
      setOrganizations(orgsRes.data);
      setVisitorStats(visitorsRes.data);
    } catch (error) {
      toast.error('Failed to fetch admin data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const handleToggleBlock = async (orgId) => {
    try {
      const res = await api.post(`/admin/organizations/${orgId}/toggle-block`);
      toast.success(res.data.message);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to toggle block');
    }
  };

  const handleActivate = async (orgId) => {
    try {
      const res = await api.post(`/admin/organizations/${orgId}/activate`);
      toast.success(res.data.message);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to activate');
    }
  };

  const handleToggleLoyalty = async (orgId) => {
    try {
      const res = await api.post(`/admin/organizations/${orgId}/toggle-loyalty`);
      toast.success(res.data.message);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to toggle loyalty');
    }
  };

  const exportSignupsCSV = () => {
    if (!organizations.length) return;
    const headers = ['Business Name', 'Owner Name', 'Email', 'Phone', 'Plan', 'Status', 'Users', 'Signed Up'];
    const rows = organizations.map(org => [
      org.name || '',
      org.owner_name || '',
      org.owner_email || '',
      org.owner_phone || '',
      org.subscription_plan || 'None',
      org.is_blocked ? 'Blocked' : org.trial_expired ? 'Trial Expired' : org.subscription_status === 'active' && org.payment_verified ? 'Paid' : org.subscription_status || 'N/A',
      org.users_count || 0,
      org.created_at ? new Date(org.created_at).toLocaleDateString() : ''
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `jaminventra_signups_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${organizations.length} signups`);
  };

  // Check if user is super admin
  if (user?.role !== 'super_admin') {
    return (
      <div className="space-y-6" data-testid="admin-page">
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <AlertTriangle className="w-12 h-12 text-[#FF3B30] mb-4" />
          <h2 className="text-heading text-xl text-[#0A0A0A]">Access Denied</h2>
          <p className="text-[#0A0A0A]/60 mt-2">You don't have permission to access this page.</p>
          <p className="text-[#0A0A0A]/40 text-sm mt-1">Super Admin role required.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007AFF]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <Crown className="w-6 h-6 text-[#FF9500]" />
          <h1 className="text-heading-black text-3xl lg:text-4xl text-[#0A0A0A] tracking-tight">SUPER ADMIN</h1>
        </div>
        <p className="text-[#0A0A0A]/60 mt-1">Platform-wide management and analytics</p>
      </div>

      {/* System Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-overline text-[#0A0A0A]/50 mb-2">Organizations</p>
                <p className="kpi-number text-3xl text-[#0A0A0A]">{stats?.total_organizations || 0}</p>
              </div>
              <div className="w-10 h-10 bg-[#007AFF]/10 rounded-sm flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#007AFF]" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-overline text-[#0A0A0A]/50 mb-2">Total Users</p>
                <p className="kpi-number text-3xl text-[#0A0A0A]">{stats?.total_users || 0}</p>
              </div>
              <div className="w-10 h-10 bg-[#34C759]/10 rounded-sm flex items-center justify-center">
                <Users className="w-5 h-5 text-[#34C759]" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-overline text-[#0A0A0A]/50 mb-2">Total Items</p>
                <p className="kpi-number text-3xl text-[#0A0A0A]">{stats?.total_items || 0}</p>
              </div>
              <div className="w-10 h-10 bg-[#FF9500]/10 rounded-sm flex items-center justify-center">
                <Package className="w-5 h-5 text-[#FF9500]" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-overline text-[#0A0A0A]/50 mb-2">Total Movements</p>
                <p className="kpi-number text-3xl text-[#0A0A0A]">{stats?.total_movements || 0}</p>
              </div>
              <div className="w-10 h-10 bg-[#007AFF]/10 rounded-sm flex items-center justify-center">
                <Activity className="w-5 h-5 text-[#007AFF]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Visitor Stats (Last 7 Days) */}
      {visitorStats.length > 0 && (
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardHeader className="border-b border-[#E5E5EA] pb-4">
            <CardTitle className="text-heading text-lg">Website Visitors (Last 7 Days)</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-3">
              {visitorStats.map((day) => (
                <div key={day.date} className="flex items-center gap-4">
                  <span className="text-sm font-mono text-[#0A0A0A]/60 w-24 shrink-0">{day.date}</span>
                  <div className="flex-1 bg-[#F5F5F7] rounded-full h-6 overflow-hidden">
                    <div className="bg-[#007AFF] h-full rounded-full flex items-center px-2" style={{ width: `${Math.min(100, (day.total / Math.max(...visitorStats.map(d => d.total))) * 100)}%`, minWidth: '30px' }}>
                      <span className="text-xs text-white font-bold">{day.total}</span>
                    </div>
                  </div>
                  <div className="text-xs text-[#0A0A0A]/40 w-40 shrink-0 truncate">
                    {Object.entries(day.pages).slice(0, 3).map(([p, c]) => `${p}(${c})`).join(' ')}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subscription Stats */}
      <Card className="border border-[#E5E5EA] shadow-none">
        <CardHeader className="border-b border-[#E5E5EA] pb-4">
          <CardTitle className="text-heading text-lg">Subscription Overview</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-3 gap-6">
            <div className="text-center p-4 bg-[#FF9500]/5 rounded-lg border border-[#FF9500]/20">
              <p className="text-overline text-[#0A0A0A]/50 mb-2">Trial</p>
              <p className="text-3xl font-mono font-bold text-[#FF9500]">{stats?.subscriptions?.trial || 0}</p>
            </div>
            <div className="text-center p-4 bg-[#34C759]/5 rounded-lg border border-[#34C759]/20">
              <p className="text-overline text-[#0A0A0A]/50 mb-2">Active</p>
              <p className="text-3xl font-mono font-bold text-[#34C759]">{stats?.subscriptions?.active || 0}</p>
            </div>
            <div className="text-center p-4 bg-[#FF3B30]/5 rounded-lg border border-[#FF3B30]/20">
              <p className="text-overline text-[#0A0A0A]/50 mb-2">Cancelled</p>
              <p className="text-3xl font-mono font-bold text-[#FF3B30]">{stats?.subscriptions?.cancelled || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Organizations Table */}
      <Card className="border border-[#E5E5EA] shadow-none">
        <CardHeader className="border-b border-[#E5E5EA] pb-4 flex flex-row items-center justify-between">
          <CardTitle className="text-heading text-lg flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#007AFF]" />
            All Organizations
          </CardTitle>
          <Button variant="outline" size="sm" onClick={exportSignupsCSV} disabled={!organizations.length} data-testid="export-signups-btn">
            <Download className="w-4 h-4 mr-1" />Export CSV
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {organizations.length === 0 ? (
            <div className="p-6 text-center text-[#0A0A0A]/50">
              No organizations found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[#E5E5EA]">
                  <TableHead className="text-overline text-[#0A0A0A]/50">Business</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Owner</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Contact</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Plan</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Status</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50 text-center">Loyalty</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Signed Up</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {organizations.map((org) => (
                  <TableRow key={org.id} className="border-b border-[#E5E5EA]">
                    <TableCell>
                      <div>
                        <p className="font-medium">{org.name}</p>
                        <p className="text-xs text-[#0A0A0A]/40">{org.users_count} user(s)</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium text-sm">{org.owner_name || 'N/A'}</p>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm">{org.owner_email || '-'}</p>
                        {org.owner_phone && <p className="text-xs text-[#007AFF]">{org.owner_phone}</p>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{org.subscription_plan || 'None'}</Badge>
                    </TableCell>
                    <TableCell>
                      {org.is_blocked ? (
                        <Badge className="bg-[#FF3B30]/10 text-[#FF3B30]">Blocked</Badge>
                      ) : org.trial_expired ? (
                        <Badge className="bg-[#FF3B30]/10 text-[#FF3B30]">Trial Expired</Badge>
                      ) : (
                        <Badge className={
                          org.subscription_status === 'active' && org.payment_verified ? 'bg-[#34C759]/10 text-[#34C759]' :
                          org.subscription_status === 'trial' ? 'bg-[#FF9500]/10 text-[#FF9500]' :
                          'bg-[#FF3B30]/10 text-[#FF3B30]'
                        }>
                          {org.subscription_status === 'trial' && org.days_left !== null
                            ? `Trial (${org.days_left}d left)`
                            : org.subscription_status === 'active' && org.payment_verified
                            ? 'Paid'
                            : org.subscription_status === 'active'
                            ? 'Active'
                            : org.subscription_status || 'N/A'}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button variant="ghost" size="sm" className={`h-8 px-2 ${org.loyalty_feature_enabled ? 'text-[#FFD700] hover:text-[#FFD700]' : 'text-[#0A0A0A]/20 hover:text-[#FFD700]'}`} onClick={() => handleToggleLoyalty(org.id)} title={org.loyalty_feature_enabled ? 'Disable loyalty' : 'Enable loyalty'} data-testid={`toggle-loyalty-${org.id}`}>
                        <Star className={`w-4 h-4 ${org.loyalty_feature_enabled ? 'fill-[#FFD700]' : ''}`} />
                      </Button>
                    </TableCell>
                    <TableCell className="text-[#0A0A0A]/60 text-sm">{formatDate(org.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {org.subscription_status !== 'active' && (
                          <Button variant="ghost" size="sm" className="text-[#34C759] hover:text-[#34C759] h-8 px-2" onClick={() => handleActivate(org.id)} title="Activate subscription" data-testid={`activate-${org.id}`}>
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className={`h-8 px-2 ${org.is_blocked ? 'text-[#34C759] hover:text-[#34C759]' : 'text-[#FF3B30] hover:text-[#FF3B30]'}`} onClick={() => handleToggleBlock(org.id)} title={org.is_blocked ? 'Unblock' : 'Block'} data-testid={`toggle-block-${org.id}`}>
                          {org.is_blocked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
